import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "react-bootstrap/lib/Button";
import NodeTypeSelect from "./NodeTypeSelect";
import TotalCounts from "./TotalCounts";
import {
    type SearchScopeEnum,
    type SearchFilterDTO,
    type SearchNodeTypeEnum,
    SearchNodeType,
} from "data/types";
import maskStyles from "components/SearchMask/Mask.module.css";
import styles from "./MainSearch.module.css";
import { isFeatureOn, Feature, isMiniView } from "data/constants";
import Icons from "@hs/icons";
import { searchAbortAction } from "data/actions";
import history from "data/history";
import SearchMask from "components/SearchMask";
import { usePersistSetting, useUpdateEffect } from "hooks";
import { produce } from "immer";
import Formsy from "formsy-react";
import { getLocalizedText, getClassNames } from "data/utils";
import Status from "components/Status";
import Constants from "data/constants";
import {
    searchCountStatusSelector,
    isOnlineSelector,
} from "data/reducers/selectors";
import { searchRouteWithItem } from "components/ItemUriLink";

const {
    getValueType,
    fixFilterValue,
    handleItemTypeChange,
    DEFAULT_ADVANCED_SEARCH_TEXT,
} = SearchMask;

type Props = {
    /** initial input text */
    searchText: string,
    /** initial search scope */
    searchScope: SearchScopeEnum,
    /** initial search scope itemUri */
    itemUri: string,
    /** callback when user starts the search using "simple" text search
     * @param {string | ISearchText} searchText the input text or advanced search text
     * @param {SearchScopeEnum} searchScope the scope to search in
     * @param {string} itemUri the itemUri selected for the scope
     * @param {Array<SearchFilterDTO>} filters search filters
     * @param {SearchNodeTypeEnum} nodeType elastic search node type
     */
    onSearch: (
        searchText: string | ISearchText,
        searchScope: SearchScopeEnum,
        itemUri: string,
        filters: Array<SearchFilterDTO>,
        nodeType: SearchNodeTypeEnum
    ) => void,
    /** calback when user changes any search parameters */
    onClear: () => void,
};

const MainSearch = ({
    searchText: searchTextFromProps,
    searchScope: searchScopeFromProps,
    itemUri: itemUriFromProps,
    onSearch,
    onClear,
    prefix = "ms",
    filters: filtersFromProps = [],
}: Props) => {
    const dispatch = useDispatch();
    const status = useSelector(searchCountStatusSelector);
    const isOnline = useSelector(isOnlineSelector);
    const [searchText, setSearchText] = useState(searchTextFromProps);
    const [searchScope, setSearchScope] = useState(searchScopeFromProps);
    const [itemUri, setItemUri] = useState(itemUriFromProps);
    const [filters, setFilters] = useState(filtersFromProps);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [canSubmit, setCanSubmit] = useState(false);

    useEffect(() => {
        switch (status) {
            case Constants.OK:
                setIsSubmitting(false);
                break;
            case Constants.LOADING:
                setIsSubmitting(true);
                break;
            case Constants.ERROR:
                setIsSubmitting(false);
                break;
            default:
                break;
        }
    }, [status]);

    const [showSearchText] = usePersistSetting({
        name: "showSearchText",
        defaultValue: false,
    });
    const [nodeType, setNodeType] = usePersistSetting({
        name: "searchNodeType",
        defaultValue: SearchNodeType.AllNodes,
    });

    const toggleNodeType = useCallback(
        () =>
            setNodeType(
                nodeType === SearchNodeType.AllNodes
                    ? SearchNodeType.LeafNode
                    : SearchNodeType.AllNodes
            ),
        [nodeType, setNodeType]
    );

    useEffect(() => {
        onClear();
    }, [searchText, searchScope, itemUri, onClear]);

    useUpdateEffect(() => {
        setSearchText(showSearchText ? DEFAULT_ADVANCED_SEARCH_TEXT : "");
    }, [showSearchText]);

    useEffect(() => {
        setTimeout(() => {
            document
                ?.getElementById(showSearchText ? "words" : "mainSearch")
                ?.focus();
        }, 0);
    }, [showSearchText]);

    const handleScopeChange = useCallback((searchScope, itemUri) => {
        setSearchScope(searchScope);
        setItemUri(itemUri);
    }, []);
    const handleTextChange = useCallback((value) => {
        setSearchText(value);
    }, []);
    const handleSearchTextChange = useCallback(
        (field: string, value: any) =>
            setSearchText((searchText) =>
                produce(searchText, (draft) => {
                    if (typeof draft === "string") {
                        draft = DEFAULT_ADVANCED_SEARCH_TEXT;
                    }
                    draft[field.split(".")[1]] = value;
                })
            ),
        []
    );
    const handleFilterChange = useCallback(
        (field: string, value: any) =>
            setFilters((filters) =>
                produce(filters, (draft) => {
                    const name = field.split(".")[1];
                    const index = draft.findIndex(
                        (filter) => filter.name === name
                    );
                    if (index === -1) {
                        draft.push({
                            name,
                            valueType: getValueType(name),
                            value,
                        });
                    } else {
                        draft[index].value = value;
                    }
                    if (name === "ItemType") return handleItemTypeChange(draft);
                })
            ),
        []
    );
    const handleSearch = useCallback(() => {
        dispatch(searchAbortAction());
        const preparedFilters = produce(filters, (draft) =>
            draft
                .map((f) => ({
                    ...f,
                    value: fixFilterValue(f.value),
                }))
                .filter((f) => !!f.value)
        );
        onSearch(
            searchText,
            searchScope,
            itemUri,
            preparedFilters,
            [],
            nodeType
        );
    }, [
        onSearch,
        searchText,
        searchScope,
        itemUri,
        filters,
        nodeType,
        dispatch,
    ]);
    const handleSearchAsYouType = useCallback(
        () =>
            isFeatureOn(Feature.searchAsYouType) && isOnline && handleSearch(),
        [isOnline, handleSearch]
    );
    const handleRecentSearch = useCallback((option: SAYTOptionDTO) => {
        if (option?.item == null) {
            return;
        }

        history.push(
            searchRouteWithItem(option.item, isMiniView(window.location))
        );
    }, []);
    useUpdateEffect(() => {
        dispatch(searchAbortAction());
        handleSearchAsYouType();
    }, [handleSearchAsYouType, dispatch]);

    const enableSubmit = useCallback(() => setCanSubmit(true), []);
    const disableSubmit = useCallback(() => setCanSubmit(false), []);
    return (
        <Formsy
            className={getClassNames(
                maskStyles.form,
                styles.form,
                "search-op-hidden"
            )}
            onValidSubmit={handleSearch}
            onValid={enableSubmit}
            onInvalid={disableSubmit}
        >
            <div className={styles.sticky}>
                <SearchMask.InputWithFormsy
                    value={typeof searchText === "string" ? searchText : ""}
                    name={`${prefix}.mainSearch`}
                    id="mainSearch"
                    onChange={handleTextChange}
                    className={styles.input}
                    disabled={showSearchText || !isOnline}
                    data-test="MainSearch.Input"
                >
                    <Icons.Library
                        name="magnifying-glass"
                        title={getLocalizedText("$search:searchButton")}
                        onClick={
                            !(isSubmitting || showSearchText)
                                ? handleSearch
                                : undefined
                        }
                    />
                </SearchMask.InputWithFormsy>
                <div className={styles.totalCountsContainer}>
                    {filters.length > 0 && (
                        <NodeTypeSelect
                            checked={nodeType === SearchNodeType.LeafNode}
                            onChange={toggleNodeType}
                        />
                    )}
                    {isFeatureOn(Feature.searchStatistics) && (
                        <TotalCounts
                            className={styles.totalCounts}
                            searchText={searchText}
                            filters={filters}
                            searchScope={searchScope}
                            itemUri={itemUri}
                            nodeType={nodeType}
                        />
                    )}
                </div>
            </div>
            <SearchMask.Recents
                sayt={typeof searchText === "string" ? searchText : ""}
                itemUri={itemUri}
                onSearch={handleRecentSearch}
                defaultExpanded={false}
            />
            <>
                <SearchMask.Advanced
                    idPrefix="main"
                    onSearchTextChange={handleSearchTextChange}
                    defaultExpanded={false}
                    searchText={
                        typeof searchText === "string"
                            ? DEFAULT_ADVANCED_SEARCH_TEXT
                            : searchText
                    }
                    prefix={prefix}
                />
                <SearchMask.Filters
                    itemUri={itemUri}
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    defaultExpanded={filters.length > 0}
                    prefix={prefix}
                />
            </>
            <SearchMask.SearchScope
                itemUri={itemUri}
                searchScope={searchScope}
                onScopeChange={handleScopeChange}
                defaultExpanded={true}
            />
            <Button
                disabled={
                    !canSubmit ||
                    /*istanbul ignore next*/ isSubmitting ||
                    !isOnline
                }
                type="submit"
                aria-label={getLocalizedText("$search:searchButton")}
                className={maskStyles.submit}
                bsStyle="primary"
                data-test="submitSearchButton"
            >
                {isSubmitting && (
                    <Status status={Constants.LOADING} inline={true} />
                )}{" "}
                {getLocalizedText("$search:searchButton")}
            </Button>
        </Formsy>
    );
};

export default MainSearch;
